import { isRecord, jsonArray, jsonInt, jsonNumber, jsonNumbers, jsonText } from '../../lib/json';
import { KEY_STATES } from './catalog/keyState';
import type { KeyState } from './catalog/keyState';
import { KEYPAD_SCHEMA_VERSION } from './catalog/storage';
import { KEYPAD_TYPE_KEYS } from './catalog/type';
import type { KeypadTypeKey } from './catalog/type';
import { asKeypadId, asKeypadKeyId } from './ids';
import type { Keypad, KeypadBook, KeypadKey, KeyStateMapping, LoadState } from './model';

/**
 * Reading the stored keypad book back.
 *
 * Storage is a convenience, never a requirement: anything unreadable is dropped
 * and counted rather than thrown, so a tablet with a half-written entry still
 * opens the console. What survives is checked shape by shape, which is why this
 * does not simply trust the JSON.
 */
export interface DecodedBook {
  readonly book: KeypadBook;
  /** Records that were present but unreadable, for the console to mention. */
  readonly droppedRecords: number;
}

const isKeypadType = (value: unknown): value is KeypadTypeKey =>
  KEYPAD_TYPE_KEYS.some((type) => type === value);

const isKeyState = (value: unknown): value is KeyState =>
  KEY_STATES.some((state) => state === value);

const decodeLoadState = (value: unknown): LoadState | null => {
  if (!isRecord(value)) return null;
  const targetId = jsonText(value.targetId);
  const amount = jsonInt(value.value);
  if (targetId === '' || amount === null) return null;
  return { targetId, value: amount };
};

const decodeMapping = (value: unknown): KeyStateMapping | null => {
  if (!isRecord(value)) return null;
  if (!isKeyState(value.state)) return null;
  const loads = jsonArray(value.loads).flatMap((entry) => {
    const decoded = decodeLoadState(entry);
    return decoded === null ? [] : [decoded];
  });
  return { state: value.state, loads };
};

const decodeKey = (value: unknown): KeypadKey | null => {
  if (!isRecord(value)) return null;
  const id = jsonText(value.id);
  const index = jsonInt(value.index);
  if (id === '' || index === null || index < 0) return null;

  const byState = new Map<KeyState, KeyStateMapping>();
  for (const entry of jsonArray(value.states)) {
    const decoded = decodeMapping(entry);
    if (decoded !== null) byState.set(decoded.state, decoded);
  }

  return {
    id: asKeypadKeyId(id),
    index,
    name: jsonText(value.name),
    states: KEY_STATES.flatMap((state) => {
      const mapping = byState.get(state);
      return mapping === undefined ? [] : [mapping];
    }),
  };
};

const decodeKeypad = (value: unknown): Keypad | null => {
  if (!isRecord(value)) return null;
  const id = jsonText(value.id);
  const type = value.type;
  const name = jsonText(value.name);
  if (id === '' || name === '' || !isKeypadType(type)) return null;

  const cat6Branch = jsonInt(value.cat6Branch);
  const keys = jsonArray(value.keys).flatMap((entry) => {
    const decoded = decodeKey(entry);
    return decoded === null ? [] : [decoded];
  });

  return {
    id: asKeypadId(id),
    type,
    name,
    address: jsonInt(value.address),
    cat6Branch,
    location: jsonText(value.location),
    keys,
    retiredAddresses: jsonNumbers(value.retiredAddresses),
    createdAt: jsonText(value.createdAt),
  };
};

const decodeWritten = (value: unknown): Readonly<Record<string, string>> => {
  if (!isRecord(value)) return {};
  const entries = Object.entries(value).flatMap(([key, entry]) => {
    const fingerprint = jsonText(entry);
    return fingerprint === '' ? [] : [[key, fingerprint] as const];
  });
  return Object.fromEntries(entries);
};

/**
 * Decodes a stored book. A book from a newer schema is ignored whole — guessing
 * at a shape this build does not know is worse than starting from nothing — while
 * unreadable panels inside a book this build understands are dropped by name.
 */
export const decodeBook = (value: unknown): DecodedBook | null => {
  if (!isRecord(value)) return null;
  const schemaVersion = jsonNumber(value.schemaVersion);
  if (schemaVersion !== KEYPAD_SCHEMA_VERSION) return null;

  const rawKeypads = jsonArray(value.keypads);
  const keypads = rawKeypads.flatMap((entry) => {
    const decoded = decodeKeypad(entry);
    return decoded === null ? [] : [decoded];
  });

  return {
    book: {
      schemaVersion: KEYPAD_SCHEMA_VERSION,
      keypads,
      written: decodeWritten(value.written),
      updatedAt: jsonText(value.updatedAt),
    },
    droppedRecords: rawKeypads.length - keypads.length,
  };
};
